import React, { useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../context/AuthContext'

/** Manages exactly one named buyer-demo account (raj@arcadetherapeutics.com)
 * via the existing, tested POST /users, POST /mfa/demo-access/grant, POST
 * /mfa/demo-access/revoke, and GET /mfa/demo-access/status endpoints — no
 * token is ever read, logged, or handled by this component; the shared
 * api() helper attaches the current admin's own session automatically, the
 * same as every other admin page. Only rendered for role === 'super_admin';
 * every underlying endpoint already independently enforces its own role
 * check regardless of this UI-level restriction.
 *
 * "Restore access" always re-grants a full, fresh 7-day window from the
 * moment it's clicked (grant_demo_mfa_bypass resets, never stacks, the
 * expiry) and reactivates the account if a previous grant was revoked or
 * left to expire — it never just extends whatever was there before. */

const RAJ_EMAIL = 'raj@arcadetherapeutics.com'
const RAJ_NAME = 'Raj Amin'
const DEMO_HOURS = 168

function generateStrongPassword(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '9').replace(/\//g, '8').replace(/=/g, '')
}

interface DemoAccessStatus {
  user_id: string
  email: string
  name: string
  role: string
  account_status: string
  is_demo: boolean
  computed_status: 'none' | 'active' | 'expired' | 'revoked'
  granted_at: string | null
  expires_at: string | null
  remaining_seconds: number | null
}

const STATUS_BADGE: Record<DemoAccessStatus['computed_status'], string> = {
  active: 'badge-green',
  expired: 'badge-amber',
  revoked: 'badge-red',
  none: 'badge-grey',
}

const STATUS_LABEL: Record<DemoAccessStatus['computed_status'], string> = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
  none: 'No account',
}

function formatRemaining(seconds: number | null): string {
  if (seconds == null || seconds <= 0) return '—'
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  if (days > 0) return `${days}d ${hours}h remaining`
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${minutes}m remaining`
}

export function DemoAccessPanel() {
  const { me } = useAuth()
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState<DemoAccessStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [newPassword, setNewPassword] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  // True when the currently authenticated admin *is* the demo account this
  // panel would otherwise manage (e.g. Raj himself, browsing /admin/overview
  // with his own full Super Admin session). He must keep every other Super
  // Admin capability — this hides only his own demo-access lifecycle
  // controls. Mirrors the backend's own self-target guard on GET
  // /mfa/demo-access/status (and the pre-existing ones on grant/revoke) —
  // this is UI-layer politeness (skip the doomed fetch, show nothing rather
  // than an error), not the actual enforcement.
  const [isSelf, setIsSelf] = useState(false)

  async function refresh() {
    setLoading(true)
    setError(null)
    try {
      const users: { id: string; email: string }[] = await api('/users')
      const raj = users.find((u) => u.email === RAJ_EMAIL)
      if (!raj) {
        setStatus(null)
      } else if (raj.id === me?.id) {
        setIsSelf(true)
      } else {
        const s = await api(`/mfa/demo-access/status?user_id=${raj.id}`)
        setStatus(s)
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load demo access status.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() }, [])

  // UI-level restriction only — every endpoint this panel calls
  // independently requires an admin role server-side regardless.
  if (me?.role !== 'super_admin') return null
  // Nothing renders here for the demo account itself — not even a loading
  // placeholder — while we're still finding out who `me` is relative to
  // Raj's account, and permanently once we know they're the same person.
  if (loading || isSelf) return null

  async function handleRestore() {
    setBusy(true)
    setError(null)
    setNewPassword(null)
    try {
      let userId = status?.user_id
      if (!userId) {
        // No account row at all (e.g. it was hard-deleted, not revoked) —
        // recreate it with the same identity and role it had before, then
        // grant. The normal case (revoked, not deleted) skips straight to
        // the grant call below.
        const password = generateStrongPassword()
        const created = await api('/users', {
          method: 'POST',
          body: JSON.stringify({ email: RAJ_EMAIL, name: RAJ_NAME, password, role: 'super_admin' }),
        })
        userId = created.id
        setNewPassword(password)
      }
      await api('/mfa/demo-access/grant', { method: 'POST', body: JSON.stringify({ user_id: userId, hours: DEMO_HOURS }) })
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not restore demo access.')
    } finally {
      setBusy(false)
    }
  }

  async function handleRevoke() {
    if (!status?.user_id) return
    setBusy(true)
    setError(null)
    try {
      await api('/mfa/demo-access/revoke', { method: 'POST', body: JSON.stringify({ user_id: status.user_id }) })
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke demo access.')
    } finally {
      setBusy(false)
    }
  }

  const computed = status?.computed_status ?? 'none'
  const canRevoke = status != null && (computed === 'active' || computed === 'expired')

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ marginTop: 0, marginBottom: 4 }}>Demo access — {RAJ_NAME}</h3>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>{RAJ_EMAIL} · Arcade Therapeutics</p>
        </div>
        <span className={`badge ${STATUS_BADGE[computed]}`}>{STATUS_LABEL[computed]}</span>
      </div>

      <div className="row-list" style={{ marginTop: 12, marginBottom: 12 }}>
        <div className="row"><span className="muted">Created</span><span>{status?.granted_at ? new Date(status.granted_at).toLocaleString() : '—'}</span></div>
        <div className="row"><span className="muted">Expires</span><span>{status?.expires_at ? new Date(status.expires_at).toLocaleString() : '—'}</span></div>
        {computed === 'active' && (
          <div className="row"><span className="muted">Remaining</span><span>{formatRemaining(status?.remaining_seconds ?? null)}</span></div>
        )}
      </div>

      {newPassword && (
        <div className="card" style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 13.5, fontWeight: 700, margin: '0 0 8px' }}>
            New account created — this password is shown once and is not saved anywhere. Copy it now.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code style={{ fontWeight: 700 }}>{newPassword}</code>
            <button className="btn small outline" onClick={() => { navigator.clipboard.writeText(newPassword); setCopied(true) }}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {error && <p className="form-error" role="alert" style={{ marginBottom: 12 }}>{error}</p>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn small" disabled={busy} onClick={handleRestore}>
          {busy ? 'Working…' : computed === 'active' ? 'Restore access (new 7-day window)' : 'Restore 7-day access'}
        </button>
        {canRevoke && (
          <button className="btn small outline" style={{ borderColor: '#a3341f', color: '#a3341f' }} disabled={busy} onClick={handleRevoke}>
            Revoke Access
          </button>
        )}
      </div>
    </div>
  )
}
